<template>
  <div class="td-view">
    <div class="td-scroll">
      <div class="td-column">
        <button class="td-back" @click="router.push('/inquilino/solicitudes')"><span class="mdi mdi-arrow-left"></span> Mis solicitudes</button>

        <div v-if="loading" class="td-state"><span class="mdi mdi-loading td-state__spinner"></span></div>
        <div v-else-if="notFound" class="td-state td-state--message">
          <span class="mdi mdi-alert-circle-outline td-state__icon"></span>
          <p>No encontramos esta solicitud.</p>
        </div>

        <template v-else-if="ticket">
          <header class="td-head">
            <div class="td-head__top">
              <span class="td-code">{{ ticket.code }}</span>
              <span class="td-date">{{ formatDate(ticket.createdAt) }}</span>
            </div>
            <h2 class="td-title">{{ ticket.title }}</h2>
            <div v-if="ticket.status" class="td-head__status">
              <span class="td-status" :style="{ background: STATUS[ticket.status].bg, color: STATUS[ticket.status].color }">{{ STATUS[ticket.status].label }}</span>
              <span v-if="ticket.technicianName" class="td-tech">Técnico: {{ ticket.technicianName }}</span>
              <span v-else-if="ticket.status !== 'RESOLVED'" class="td-tech td-tech--pending">Técnico por asignar</span>
            </div>
          </header>

          <section>
            <h3 class="td-label">Descripción</h3>
            <p class="td-description">{{ ticket.description }}</p>
            <div v-if="ticket.photos.length" class="td-photos">
              <a v-for="p in ticket.photos" :key="p.id" :href="`${UPLOADS_BASE}/${p.fileUrl}`" target="_blank" rel="noopener" class="td-photo">
                <img :src="`${UPLOADS_BASE}/${p.fileUrl}`" alt="Foto adjunta" />
              </a>
            </div>
          </section>

          <section>
            <h3 class="td-label">Actualizaciones</h3>
            <div v-if="!ticket.updates.length" class="td-empty">Aún no hay actualizaciones.</div>
            <div v-else class="td-updates">
              <template v-for="u in ticket.updates" :key="u.id">
                <div v-if="u.kind === 'STATUS'" class="td-event">
                  <span class="td-event__dot"></span>
                  <span>Estatus: <strong>{{ STATUS[u.status]?.label }}</strong></span>
                  <span class="td-event__time">{{ formatDateTime(u.createdAt) }}</span>
                </div>
                <div v-else class="td-msg" :class="{ 'td-msg--mine': u.from === 'TENANT' }">
                  <div class="td-msg__meta"><span class="td-msg__author">{{ u.author }}</span><span>{{ formatDateTime(u.createdAt) }}</span></div>
                  <p v-if="u.text" class="td-msg__text">{{ u.text }}</p>
                  <a v-if="u.attachmentUrl" :href="`${UPLOADS_BASE}/${u.attachmentUrl}`" target="_blank" rel="noopener" class="td-msg__file">
                    <span class="mdi mdi-paperclip"></span> {{ u.attachmentName || 'Archivo adjunto' }}
                  </a>
                </div>
              </template>
            </div>
          </section>

          <section class="td-reply">
            <button v-if="ticket.canConfirm" class="td-confirm" :disabled="sending" @click="send(true)">
              <span class="mdi mdi-check-circle-outline"></span> Confirmar que se resolvió
            </button>
            <label class="td-label" for="td-reply">Agregar información</label>
            <textarea id="td-reply" v-model="text" class="td-textarea" rows="3" maxlength="2000" placeholder="Escribe un mensaje para el equipo del parque..."></textarea>
            <div class="td-reply__row">
              <input ref="fileInput" type="file" accept="image/*" capture="environment" class="td-file" @change="file = $event.target.files?.[0] || null" />
              <button type="button" class="td-attach" @click="fileInput.click()"><span class="mdi mdi-camera-outline"></span> Adjuntar foto</button>
              <span v-if="file" class="td-file-name">{{ file.name }}</span>
              <button class="td-send" :disabled="sending || (!text.trim() && !file)" @click="send(false)">{{ sending ? 'Enviando…' : 'Enviar' }}</button>
            </div>
            <p v-if="error" class="td-error">{{ error }}</p>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getPortalTicket, sendPortalTicketMessage } from '@/services/tenantPortal'
import { UPLOADS_BASE } from '@/components/TenantPortal/tenantNav'
import { TICKET_STATUS as STATUS, formatDate, formatDateTime, ERROR_MESSAGES } from '@/components/TenantPortal/tenantTicketUi'

const route = useRoute()
const router = useRouter()

const ticket = ref(null)
const loading = ref(true)
const notFound = ref(false)
const text = ref('')
const file = ref(null)
const fileInput = ref(null)
const sending = ref(false)
const error = ref('')

async function load() {
  try {
    ticket.value = await getPortalTicket(route.params.id)
  } catch {
    ticket.value = null
    notFound.value = true
  } finally {
    loading.value = false
  }
}

async function send(confirm) {
  error.value = ''
  sending.value = true
  try {
    await sendPortalTicketMessage(route.params.id, { text: confirm ? '' : text.value.trim(), confirm, file: confirm ? null : file.value })
    text.value = ''
    file.value = null
    if (fileInput.value) fileInput.value.value = ''
    await load()
  } catch (err) {
    error.value = ERROR_MESSAGES[err.message] || 'No se pudo enviar. Intenta de nuevo.'
  } finally {
    sending.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.td-view { flex: 1; min-height: 0; display: flex; flex-direction: column; background: #fff; }
.td-scroll { flex: 1; overflow-y: auto; padding: 16px 16px 32px; }
.td-column { max-width: 720px; margin: 0 auto; display: flex; flex-direction: column; gap: 20px; }
.td-back { align-self: flex-start; border: none; background: none; padding: 0; color: #64748b; font: 500 12px Inter, sans-serif; cursor: pointer; display: flex; align-items: center; gap: 4px; }
.td-back:hover { color: #1e3a5f; }
.td-state { display: flex; flex-direction: column; align-items: center; gap: 10px; padding: 32px 16px; color: #94a3b8; }
.td-state--message { text-align: center; font: 400 13px Inter, sans-serif; }
.td-state__icon { font-size: 32px; }
.td-state__spinner { font-size: 28px; animation: td-spin 1s linear infinite; }
@keyframes td-spin { to { transform: rotate(360deg); } }

.td-head { display: flex; flex-direction: column; gap: 8px; }
.td-head__top { display: flex; justify-content: space-between; }
.td-code { font: 400 12px var(--font-mono); color: #94a3b8; }
.td-date { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.td-title { margin: 0; font: 600 16px/1.35 Inter, sans-serif; color: #1e3a5f; }
.td-head__status { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.td-status { height: 28px; padding: 0 16px; display: flex; align-items: center; border-radius: 14px; font: 500 12px Inter, sans-serif; }
.td-tech { font: 400 12px Inter, sans-serif; color: #64748b; }
.td-tech--pending { color: #b45309; }

.td-label { margin: 0 0 8px; font: 500 11px Inter, sans-serif; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; }
.td-description { margin: 0; font: 400 13px/1.6 Inter, sans-serif; color: #1e3a5f; white-space: pre-wrap; }
.td-photos { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
.td-photo { width: 96px; height: 96px; border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0; }
.td-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.td-empty { font: 400 12px Inter, sans-serif; color: #94a3b8; text-align: center; padding: 12px; }

.td-updates { display: flex; flex-direction: column; gap: 10px; }
.td-event { display: flex; align-items: center; gap: 8px; font: 400 12px Inter, sans-serif; color: #64748b; }
.td-event__dot { width: 8px; height: 8px; border-radius: 50%; background: #cbd5e1; flex-shrink: 0; }
.td-event__time { margin-left: auto; font-size: 11px; color: #94a3b8; }
.td-msg { align-self: flex-start; max-width: 88%; padding: 10px 12px; border-radius: 10px; background: #f1f5f9; display: flex; flex-direction: column; gap: 4px; }
.td-msg--mine { align-self: flex-end; background: #eff6ff; }
.td-msg__meta { display: flex; justify-content: space-between; gap: 12px; font: 400 11px Inter, sans-serif; color: #94a3b8; }
.td-msg__author { font-weight: 500; color: #64748b; }
.td-msg__text { margin: 0; font: 400 13px/1.5 Inter, sans-serif; color: #1e3a5f; white-space: pre-wrap; word-break: break-word; }
.td-msg__file { font: 500 12px Inter, sans-serif; color: #3b82f6; text-decoration: none; word-break: break-all; }

.td-reply { display: flex; flex-direction: column; gap: 8px; padding-top: 16px; border-top: 1px solid #f1f5f9; }
.td-confirm { height: 40px; border: 1px solid #bbf7d0; border-radius: 8px; background: #f0fdf4; color: #15803d; font: 500 13px Inter, sans-serif; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; margin-bottom: 8px; }
.td-confirm:disabled { opacity: 0.6; }
.td-textarea { width: 100%; padding: 10px; border: 1px solid #e2e8f0; border-radius: 6px; font: 400 13px Inter, sans-serif; color: #1e3a5f; resize: vertical; }
.td-textarea:focus { outline: none; border-color: #3b82f6; }
.td-reply__row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.td-file { display: none; }
.td-attach { height: 34px; padding: 0 12px; border: 1px dashed #94a3b8; border-radius: 6px; background: #fff; color: #64748b; font: 500 12px Inter, sans-serif; cursor: pointer; display: flex; align-items: center; gap: 6px; }
.td-file-name { font: 400 12px Inter, sans-serif; color: #1e3a5f; word-break: break-all; }
.td-send { margin-left: auto; height: 34px; padding: 0 18px; border: none; border-radius: 6px; background: #3b82f6; color: #fff; font: 500 13px Inter, sans-serif; cursor: pointer; }
.td-send:disabled { opacity: 0.5; cursor: not-allowed; }
.td-error { margin: 0; font: 400 12px Inter, sans-serif; color: #b91c1c; }
</style>